// Vista ampliada de una foto del lanzamiento —
// ADR-2026-09-07-lanzamientos-visor-de-foto.
//
// Es a propósito lo más chico que un visor puede ser: la foto en resolución
// completa, y la × para cerrar. Nada más. Acá la foto ya está elegida y el
// trabajo es la ficha; cualquier acción de más sería una segunda pantalla de
// creaciones metida adentro del formulario.
//
// El fondo NO es la foto desenfocada como en creaciones: es la pantalla que
// quedó atrás, borroneada y oscurecida. En creaciones la foto ocupa toda la
// pantalla y el fondo tiene que llenarse con algo; acá el visor sale sobre la
// ficha, y dejarla ver detrás es lo que dice "esto es una mirada de cerca, no
// te fuiste a ningún lado".
//
// Portaleado a `document.body`: el panel de contenido de escritorio es
// `position: fixed` y un overlay montado adentro queda por debajo de la barra
// lateral (project_fixed_panel_stacking_trap).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';

export default function VisorDeFoto({
  url,
  esVideo = false,
  onCerrar,
}: {
  /** URL firmada del archivo en resolución completa. `null` = todavía firmando. */
  url: string | null;
  /**
   * Es un video: se reproduce en vez de dibujarse. Un `<img>` apuntando a un mp4
   * es el ícono roto, que es el mismo modo de falla de las miniaturas sin póster
   * (ADR-2026-09-15-un-video-subido-necesita-su-poster).
   */
  esVideo?: boolean;
  onCerrar: () => void;
}) {
  const [cargada, setCargada] = useState(false);

  // La foto cambia (se pasó a otra) → vuelve el spinner hasta que baje la nueva.
  useEffect(() => setCargada(false), [url]);

  useEffect(() => {
    const alaTecla = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    document.addEventListener('keydown', alaTecla);
    // El fondo no scrollea mientras el visor está abierto: en el teléfono, el
    // scroll del dedo sobre la foto movía la ficha de atrás.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alaTecla);
      document.body.style.overflow = overflow;
    };
  }, [onCerrar]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={esVideo ? 'Video ampliado' : 'Foto ampliada'}
      onClick={onCerrar}
      className="animate-visor-entra fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
    >
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar la vista ampliada"
        className="absolute top-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="w-5 h-5" />
      </button>

      {!cargada && <Loader2 className="w-6 h-6 animate-spin text-white/70" />}

      {url && esVideo && (
        <video
          src={url}
          controls
          autoPlay
          loop
          playsInline
          onLoadedData={() => setCargada(true)}
          onClick={(e) => e.stopPropagation()}
          className={`max-h-[88vh] max-w-[92vw] rounded-xl transition-opacity duration-200 ${
            cargada ? 'opacity-100' : 'absolute opacity-0'
          }`}
        />
      )}

      {url && !esVideo && (
        <img
          src={url}
          alt=""
          onLoad={() => setCargada(true)}
          onClick={(e) => e.stopPropagation()}
          // Oculta hasta que bajó entera: mostrarla mientras carga la dibuja por
          // bandas y, con la resolución completa, eso dura bastante.
          className={`max-h-[88vh] max-w-[92vw] rounded-xl object-contain transition-opacity duration-200 ${
            cargada ? 'opacity-100' : 'absolute opacity-0'
          }`}
        />
      )}
    </div>,
    document.body,
  );
}
